import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  ArrowDownTrayIcon,
  ClockIcon,
  FolderIcon,
  FunnelIcon,
  HomeIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  Squares2X2Icon,
  Bars3Icon,
  TrashIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { deleteDocument, fetchDocuments, updateDocument } from "../../../../api/document";
import { getProjectFoldersAsync } from "../../../../slices/project/folder";
import {
  ancestorPath,
  buildFolderTree,
  collectSubtreeIds,
  countDescendants,
  findNode,
  isDescendant,
  namePath,
} from "../../../utils/moduleSelectors/projectFolderTreeUtils";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import { documentSrc } from "../../documents/utils";
import { PMDialog } from "../../../utils/PMDialog";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import { FolderCreateDialog } from "./folder-create-dialog";
import { FolderActionsMenu } from "./folder-actions-menu";
import {
  FolderAccessDialog,
  FolderDeleteDialog,
  FolderRenameDialog,
  folderRecord,
} from "./folder-edit-dialogs";
import {
  isAdminsFolder,
} from "./folder-access-fields";
import {
  deleteProjectFolderAsync,
  updateProjectFolderAsync,
} from "../../../../slices/project/folder";
import {
  ARCHIVE_ACCEPT,
  FOLDER_TINTS,
  countFilesInFolder,
  countFilesInFolders,
  documentDisplayName,
  downloadDocumentFile,
  fileTypeStyle,
  formatFileSize,
  isDocumentInFolder,
  readArchivePrefs,
  relativeTime,
  writeArchivePrefs,
} from "./archive-utils";

function folderTint(index) {
  return FOLDER_TINTS[index % FOLDER_TINTS.length];
}

function FolderGlyph({ color }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinejoin="round"
    >
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function FileGlyph({ ext, color, tint, large = false }) {
  return (
    <div
      className={`rounded-lg bg-white shadow-[0_4px_10px_-4px_rgba(16,34,31,0.25)] flex items-end justify-center ${
        large ? "h-[86px] w-[68px] rounded-[10px] pb-3" : "h-16 w-[52px] pb-2"
      }`}
      style={{ color }}
    >
      <span
        className="font-semibold tracking-wide"
        style={{ fontSize: large ? 12 : 10, color }}
      >
        {ext}
      </span>
    </div>
  );
}

export function ProjectDocumentArchive({
  project,
  initialFolderId = null,
  canManage = false,
  isArchiveAdmin = false,
  layout = "tab",
  onCurrentFolderChange,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const dispatch = useDispatch();
  const uploadPickerRef = useRef(null);

  const folderState = useSelector((store) => store.projectFolders);
  const folders = folderState?.data || [];

  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState(() => readArchivePrefs().view);
  const [nav, setNav] = useState("all");
  const [currentFolderId, setCurrentFolderId] = useState(initialFolderId);
  const [selectedId, setSelectedId] = useState(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
  const [renameFolder, setRenameFolder] = useState(null);
  const [accessFolder, setAccessFolder] = useState(null);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [savingRename, setSavingRename] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(false);

  const changeView = (next) => {
    setView(next);
    writeArchivePrefs({ view: next });
  };

  useEffect(() => {
    setCurrentFolderId(initialFolderId);
  }, [initialFolderId]);

  useEffect(() => {
    onCurrentFolderChange?.(currentFolderId || null);
  }, [currentFolderId, onCurrentFolderChange]);

  useEffect(() => {
    if (!project?.id) return;
    dispatch(
      getProjectFoldersAsync({
        project_id: project.id,
        page: 1,
        per_page: 500,
        query: "",
        sort: "asc",
        sortBy: "title",
        filters: [],
      })
    );
  }, [dispatch, project?.id, refreshKey]);

  const loadDocuments = useCallback(async () => {
    if (!project?.id) return;
    setDocsLoading(true);
    setDocsError(null);
    try {
      const response = await fetchDocuments({
        page: 1,
        per_page: 200,
        sort: "desc",
        sortBy: "updated_at",
        query: "",
        fields: "id,title,file,status,created_at,updated_at,module,module_param",
        filters: [{ name: "project_archive", value: project.id }],
      });
      if (response?.errors) {
        setDocsError(response.errors);
        setDocuments([]);
      } else {
        setDocuments(response?.documents || []);
      }
    } catch (err) {
      setDocsError([t("document_archive.load_error")]);
      setDocuments([]);
    } finally {
      setDocsLoading(false);
    }
  }, [project?.id, t]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments, refreshKey]);

  const tree = useMemo(
    () => buildFolderTree(folders, project?.id),
    [folders, project?.id]
  );

  const currentNode = useMemo(
    () => (currentFolderId ? findNode(tree, currentFolderId)?.node : null),
    [tree, currentFolderId]
  );

  useEffect(() => {
    if (!currentFolderId || !folders.length) return;
    if (!findNode(tree, currentFolderId)) {
      setCurrentFolderId(null);
    }
  }, [currentFolderId, folders.length, tree]);

  const breadcrumb = useMemo(() => {
    const projectTitle =
      project?.displayTitle || project?.name || t("document_archive.project");
    if (!currentFolderId) return [projectTitle];
    return [projectTitle, ...namePath(tree, currentFolderId)];
  }, [project, currentFolderId, tree, t]);

  const childFolders = currentNode ? currentNode.children || [] : tree;

  const q = query.trim().toLowerCase();

  const visibleFolders = useMemo(() => {
    if (nav === "recent") return [];
    const source = q
      ? folders.filter((folder) =>
          (folder.title || folder.displayTitle || "")
            .toLowerCase()
            .includes(q)
        )
      : childFolders.map((node) => node.object || node);
    return source;
  }, [nav, q, folders, childFolders]);

  const visibleFiles = useMemo(() => {
    let list = documents;
    if (nav === "recent") {
      list = [...documents].sort(
        (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
      );
    } else if (!q) {
      list = documents.filter((doc) =>
        isDocumentInFolder(doc, currentFolderId)
      );
    }
    if (q) {
      list = list.filter((doc) => {
        const name = documentDisplayName(doc).toLowerCase();
        const filename = (doc?.file?.filename || "").toLowerCase();
        return name.includes(q) || filename.includes(q);
      });
    }
    return list;
  }, [documents, nav, q, currentFolderId]);

  useEffect(() => {
    if (!visibleFiles.length) {
      setSelectedId(null);
      return;
    }
    if (!visibleFiles.some((doc) => doc.id === selectedId)) {
      setSelectedId(visibleFiles[0].id);
    }
  }, [visibleFiles, selectedId]);

  const selected = visibleFiles.find((doc) => doc.id === selectedId) || null;
  const selectedStyle = selected ? fileTypeStyle(selected) : null;

  const selectFile = (doc) => {
    if (!doc?.id) return;
    setSelectedId(doc.id);
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 1023px)").matches
    ) {
      setMobileDetailsOpen(true);
    }
  };

  useEffect(() => {
    if (!selected) setMobileDetailsOpen(false);
  }, [selected]);

  useEffect(() => {
    setEditingTitle(false);
    setTitleDraft(selected ? documentDisplayName(selected) : "");
  }, [selected?.id]);

  const openFile = (doc) => {
    const url = doc?.file?.url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.error(t("document_archive.missing_file"));
    }
  };

  const downloadFile = async (doc) => {
    if (!doc?.file?.url) {
      toast.error(t("document_archive.missing_file"));
      return;
    }
    try {
      await downloadDocumentFile(doc);
    } catch {
      toast.error(t("document_archive.download_failed"));
    }
  };

  const saveFolderAccess = async (access) => {
    const record = folderRecord(accessFolder);
    if (!record?.id || !isArchiveAdmin || !project?.id) return;
    setSavingAccess(true);
    try {
      const result = await dispatch(
        updateProjectFolderAsync({
          id: record.id,
          folder_id: record.id,
          project_id: project.id,
          access,
        })
      );
      if (result?.error || result?.payload === false) {
        toast.error(t("document_archive.access_save_failed"));
        return;
      }
      toast.success(t("document_archive.access_saved"));
      setAccessFolder(null);
      setRefreshKey((value) => value + 1);
    } catch {
      toast.error(t("document_archive.access_save_failed"));
    } finally {
      setSavingAccess(false);
    }
  };

  const saveFolderRename = async (title) => {
    const record = folderRecord(renameFolder);
    if (!record?.id || !canManage || !project?.id) return;
    if (!title) {
      toast.error(t("document_archive.folder_title_required"));
      return;
    }
    if (title === record.title) {
      setRenameFolder(null);
      return;
    }
    setSavingRename(true);
    try {
      const result = await dispatch(
        updateProjectFolderAsync({
          id: record.id,
          folder_id: record.id,
          project_id: project.id,
          title,
          description: record.description,
          parent_id: record.parent_id,
        })
      );
      if (result?.error || result?.payload === false) {
        toast.error(t("document_archive.folder_rename_failed"));
        return;
      }
      toast.success(t("document_archive.folder_renamed"));
      setRenameFolder(null);
      setRefreshKey((value) => value + 1);
    } catch {
      toast.error(t("document_archive.folder_rename_failed"));
    } finally {
      setSavingRename(false);
    }
  };

  const confirmDeleteFolder = async () => {
    const record = folderRecord(deleteFolder);
    if (!record?.id || !canManage || !project?.id) return;
    setDeletingFolder(true);
    try {
      const result = await dispatch(
        deleteProjectFolderAsync({
          id: record.id,
          project: { project_id: project.id },
        })
      );
      if (result?.error || result?.payload === false) {
        toast.error(t("document_archive.folder_delete_failed"));
        return;
      }
      if (
        String(currentFolderId) === String(record.id) ||
        isDescendant(tree, currentFolderId, record.id)
      ) {
        setCurrentFolderId(record.parent_id || null);
      }
      setDeleteFolder(null);
      toast.success(t("document_archive.folder_deleted"));
      setRefreshKey((value) => value + 1);
    } catch {
      toast.error(t("document_archive.folder_delete_failed"));
    } finally {
      setDeletingFolder(false);
    }
  };

  const saveTitle = async () => {
    if (!selected || !canManage) return;
    const next = titleDraft.trim();
    if (!next) {
      toast.error(t("document_archive.title_required"));
      return;
    }
    if (next === documentDisplayName(selected)) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      const updated = await updateDocument({ id: selected.id, title: next });
      if (updated?.id) {
        setDocuments((current) =>
          current.map((doc) =>
            doc.id === updated.id ? { ...doc, title: updated.title } : doc
          )
        );
        toast.success(t("document_archive.title_saved"));
        setEditingTitle(false);
      } else {
        toast.error(t("document_archive.title_save_failed"));
      }
    } catch {
      toast.error(t("document_archive.title_save_failed"));
    } finally {
      setSavingTitle(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected || !canManage) return;
    setDeleting(true);
    try {
      const response = await deleteDocument(selected.id);
      if (response?.error) {
        toast.error(t("document_archive.delete_failed"));
        return;
      }
      setDocuments((current) => current.filter((doc) => doc.id !== selected.id));
      setSelectedId(null);
      setDeleteConfirmOpen(false);
      toast.success(t("document_archive.deleted"));
    } catch {
      toast.error(t("document_archive.delete_failed"));
    } finally {
      setDeleting(false);
    }
  };

  const heading =
    nav === "recent"
      ? t("document_archive.recent")
      : currentNode?.name || t("document_archive.all_files");

  const resultLine = t("document_archive.result_line", {
    files: visibleFiles.length,
    folders: visibleFolders.length,
  });

  const uploadModule = currentFolderId ? "folder" : "project";
  const uploadParam = currentFolderId || project?.id;

  const navItems = [
    {
      id: "all",
      label: t("document_archive.all_files"),
      icon: HomeIcon,
      count: documents.length || "",
    },
    {
      id: "recent",
      label: t("document_archive.recent"),
      icon: ClockIcon,
      count: "",
    },
  ];

  const renderFolderCard = (folder, index) => {
    const id = folder.id;
    const name = folder.title || folder.displayTitle || folder.name;
    const tint = folderTint(index);
    const fileCount = countFilesInFolder(documents, id);
    return (
      <div
        key={id}
        className="flex items-center gap-2 rounded-[18px] border border-[#EDF0EF] bg-white pr-2 hover:border-[#CBDBD8] hover:bg-[#FAFCFB]"
      >
        <button
          type="button"
          onClick={() => {
            setNav("all");
            setCurrentFolderId(id);
          }}
          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3.5 text-left"
        >
          <div
            className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-xl"
            style={{ background: tint.tint, color: tint.color }}
          >
            <FolderGlyph color={tint.color} />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-[#10221F]">
              {name}
              {isAdminsFolder(folder) ? (
                <LockClosedIcon className="ml-1 inline h-3.5 w-3.5 text-amber-600" />
              ) : null}
            </div>
            <div className="text-xs text-[#8B9895]">
              {t("document_archive.files_count", { count: fileCount })}
            </div>
          </div>
        </button>
        <FolderActionsMenu
          canManage={canManage}
          showAccess={isArchiveAdmin}
          onRename={() => setRenameFolder(folder)}
          onAccess={() => setAccessFolder(folder)}
          onDelete={() => setDeleteFolder(folder)}
        />
      </div>
    );
  };

  const renderFileCard = (doc) => {
    const style = fileTypeStyle(doc);
    const on = selectedId === doc.id;
    const thumb = documentSrc(doc, "thumb");
    const isImage = style.ext === "PNG" || style.ext === "JPG" || style.ext === "JPEG" || style.ext === "WEBP" || style.ext === "GIF" || style.ext === "IMG";
    return (
      <button
        key={doc.id}
        type="button"
        onClick={() => selectFile(doc)}
        onDoubleClick={() => openFile(doc)}
        className="overflow-hidden rounded-[18px] border bg-white text-left"
        style={{
          borderColor: on ? "#0F766E" : "#EDF0EF",
          boxShadow: on ? "0 0 0 3px rgba(15,118,110,0.12)" : "none",
        }}
      >
        <div
          className="relative flex h-[116px] items-center justify-center"
          style={{ background: style.tint }}
        >
          {isImage && thumb ? (
            <img
              src={thumb}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <FileGlyph ext={style.ext} color={style.color} />
          )}
        </div>
        <div className="flex flex-col gap-0.5 px-3.5 py-3">
          <div className="truncate text-[13.5px] font-medium text-[#10221F]">
            {documentDisplayName(doc)}
          </div>
          <div className="text-xs text-[#8B9895]">
            {relativeTime(doc.updated_at, lang)} ·{" "}
            {formatFileSize(doc?.file?.size_kb)}
          </div>
        </div>
      </button>
    );
  };

  const renderFileRow = (doc) => {
    const style = fileTypeStyle(doc);
    const on = selectedId === doc.id;
    return (
      <button
        key={doc.id}
        type="button"
        onClick={() => selectFile(doc)}
        onDoubleClick={() => openFile(doc)}
        className="grid w-full grid-cols-[1fr_110px_96px] items-center gap-4 border-b border-[#F3F5F5] px-[18px] py-2.5 text-left text-[13.5px] text-[#4A5A57] last:border-b-0 sm:grid-cols-[1fr_110px_96px]"
        style={{ background: on ? "#F2F8F7" : "#FFFFFF" }}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] text-[9px] font-bold"
            style={{ background: style.tint, color: style.color }}
          >
            {style.ext}
          </div>
          <span className="truncate font-medium text-[#10221F]">
            {documentDisplayName(doc)}
          </span>
        </div>
        <div className="hidden sm:block">{relativeTime(doc.updated_at, lang)}</div>
        <div>{formatFileSize(doc?.file?.size_kb)}</div>
      </button>
    );
  };

  if (folderState?.isLoading && !folders.length && docsLoading) {
    return (
      <div className={layout === "page" ? "h-full" : "mt-6"}>
        <ResourceLoading />
      </div>
    );
  }

  if (folderState?.error) {
    return <ResourceError errors={folderState.errors} />;
  }

  if (docsError) {
    return <ResourceError errors={docsError} />;
  }

  return (
    <div
      className={`flex min-h-0 w-full overflow-hidden rounded-[22px] border border-[#EDF0EF] bg-white ${
        layout === "page" ? "h-full" : "h-[calc(100dvh-18rem)] min-h-[24rem]"
      }`}
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col md:flex-row">
        <aside className="hidden w-[238px] shrink-0 flex-col gap-6 overflow-y-auto border-r border-[#EDF0EF] bg-[#FBFCFC] px-4 py-[22px] md:flex">
          {layout === "page" ? null : (
            <div className="flex items-center gap-2.5 px-1.5">
              <div className="flex h-[30px] w-[30px] items-center justify-center rounded-[10px] bg-[#0F766E] text-[15px] font-bold text-white">
                A
              </div>
              <div className="text-base font-semibold tracking-tight text-[#10221F]">
                {t("document_archive.brand")}
              </div>
            </div>
          )}

          {canManage ? (
            <button
              type="button"
              onClick={() => uploadPickerRef.current?.()}
              className="flex h-[42px] items-center justify-center gap-2 rounded-[20px] bg-[#0F766E] text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(15,118,110,0.7)] hover:bg-[#0B5A54]"
            >
              <span className="text-lg leading-none">+</span>
              {t("document_archive.upload")}
            </button>
          ) : null}

          <div className="flex flex-col gap-0.5">
            {navItems.map((item) => {
              const active = nav === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setNav(item.id);
                    if (item.id === "all") setCurrentFolderId(null);
                  }}
                  className="flex h-[38px] items-center gap-2.5 rounded-[20px] px-3.5 text-sm"
                  style={{
                    background: active ? "#E6F2F0" : "transparent",
                    color: active ? "#0F766E" : "#4A5A57",
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  <Icon className="h-[17px] w-[17px]" />
                  {item.label}
                  <span className="ml-auto text-xs text-[#A6B2AF]">
                    {item.count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-2">
            <div className="px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#A6B2AF]">
              {t("document_archive.folders")}
            </div>
            {tree.length ? (
              tree.slice(0, 8).map((node, index) => {
                const tint = folderTint(index);
                const active = String(currentFolderId) === String(node.id);
                return (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => {
                      setNav("all");
                      setCurrentFolderId(node.id);
                    }}
                    className="flex h-[34px] items-center gap-2.5 rounded-[20px] px-3.5 text-left text-sm text-[#4A5A57] hover:bg-[#F0F3F2]"
                    style={{
                      background: active ? "#E6F2F0" : "transparent",
                      color: active ? "#0F766E" : "#4A5A57",
                    }}
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: tint.color }}
                    />
                    <span className="truncate">{node.name}</span>
                    {isAdminsFolder(node) ? (
                      <LockClosedIcon className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                    ) : null}
                  </button>
                );
              })
            ) : (
              <p className="px-3.5 text-xs text-[#8B9895]">
                {t("document_archive.no_folders")}
              </p>
            )}
          </div>

          <div className="mt-auto rounded-[18px] border border-[#EDF0EF] bg-white p-3.5">
            <div className="text-[13px] font-semibold text-[#10221F]">
              {t("document_archive.project_archive")}
            </div>
            <div className="mt-1 text-xs text-[#8B9895]">
              {t("document_archive.storage_hint", {
                files: documents.length,
                folders: folders.length,
              })}
            </div>
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex h-auto flex-col gap-3 border-b border-[#EDF0EF] px-4 py-4 sm:min-h-[68px] sm:flex-row sm:flex-wrap sm:items-center sm:px-6">
            <div className="flex h-10 max-w-[460px] flex-1 items-center gap-2.5 rounded-[20px] border border-transparent bg-[#F5F7F6] px-4 focus-within:border-[#0F766E]">
              <MagnifyingGlassIcon className="h-4 w-4 text-[#8B9895]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("document_archive.search_placeholder")}
                className="flex-1 bg-transparent text-sm text-[#10221F] outline-none placeholder:text-[#93A19E]"
              />
            </div>
            {canManage ? (
              <button
                type="button"
                onClick={() => setFolderDialogOpen(true)}
                className="inline-flex h-[34px] items-center gap-1.5 rounded-[20px] border border-[#E5E9E8] px-3.5 text-[13px] text-[#4A5A57] hover:bg-[#F5F7F6]"
              >
                <FolderIcon className="h-4 w-4" />
                {t("document_archive.new_folder")}
              </button>
            ) : null}
            <div className="flex gap-1.5 md:hidden">
              {navItems.map((item) => {
                const active = nav === item.id;
                return (
                  <button
                    key={`mobile-${item.id}`}
                    type="button"
                    onClick={() => {
                      setNav(item.id);
                      if (item.id === "all") setCurrentFolderId(null);
                    }}
                    className="h-[34px] rounded-[20px] px-3.5 text-[13px]"
                    style={{
                      background: active ? "#E6F2F0" : "#F5F7F6",
                      color: active ? "#0F766E" : "#4A5A57",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex min-h-0 flex-1">
            <div className="min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 sm:pb-8">
              <div className="mb-2.5 flex flex-wrap items-center gap-2 text-[13px] text-[#8B9895]">
                {breadcrumb.map((crumb, index) => (
                  <span key={`${crumb}-${index}`} className="flex items-center gap-2">
                    {index > 0 ? <span>/</span> : null}
                    <button
                      type="button"
                      className={
                        index === breadcrumb.length - 1
                          ? "font-medium text-[#10221F]"
                          : "hover:text-[#0F766E]"
                      }
                      onClick={() => {
                        if (index === 0) {
                          setNav("all");
                          setCurrentFolderId(null);
                          return;
                        }
                        const ancestors = ancestorPath(tree, currentFolderId) || [];
                        const targetId = ancestors[index - 1];
                        if (targetId) setCurrentFolderId(targetId);
                      }}
                    >
                      {crumb}
                    </button>
                  </span>
                ))}
              </div>

              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="flex items-center gap-2 text-[27px] font-semibold tracking-tight text-[#10221F]">
                    {heading}
                    {isAdminsFolder(currentNode) ? (
                      <LockClosedIcon className="h-5 w-5 text-amber-600" />
                    ) : null}
                    {currentFolderId && currentNode ? (
                      <FolderActionsMenu
                        canManage={canManage}
                        showAccess={isArchiveAdmin}
                        onRename={() => setRenameFolder(currentNode)}
                        onAccess={() => setAccessFolder(currentNode)}
                        onDelete={() => setDeleteFolder(currentNode)}
                      />
                    ) : null}
                  </h2>
                  <p className="mt-1 text-[13px] text-[#8B9895]">{resultLine}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex h-[34px] items-center gap-1.5 rounded-[20px] border border-[#E5E9E8] px-3.5 text-[13px] text-[#4A5A57]">
                    <FunnelIcon className="h-3.5 w-3.5" />
                    {t("document_archive.modified")}
                  </div>
                  <div className="flex gap-0.5 rounded-[20px] bg-[#F0F3F2] p-0.5">
                    <button
                      type="button"
                      onClick={() => changeView("grid")}
                      className="flex h-[34px] w-[34px] items-center justify-center rounded-[20px]"
                      style={{
                        background: view === "grid" ? "#FFFFFF" : "transparent",
                        color: view === "grid" ? "#0F766E" : "#8B9895",
                      }}
                      aria-label={t("document_archive.grid")}
                    >
                      <Squares2X2Icon className="h-[15px] w-[15px]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => changeView("list")}
                      className="flex h-[34px] w-[34px] items-center justify-center rounded-[20px]"
                      style={{
                        background: view === "list" ? "#FFFFFF" : "transparent",
                        color: view === "list" ? "#0F766E" : "#8B9895",
                      }}
                      aria-label={t("document_archive.list")}
                    >
                      <Bars3Icon className="h-[15px] w-[15px]" />
                    </button>
                  </div>
                </div>
              </div>

              {visibleFolders.length > 0 ? (
                <>
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#A6B2AF]">
                    {t("document_archive.folders")}
                  </div>
                  <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {visibleFolders.map((folder, index) =>
                      renderFolderCard(folder, index)
                    )}
                  </div>
                </>
              ) : null}

              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-[#A6B2AF]">
                {t("document_archive.files")}
              </div>

              {docsLoading && !documents.length ? (
                <ResourceLoading />
              ) : visibleFiles.length === 0 ? (
                <p className="rounded-[18px] border border-dashed border-[#EDF0EF] px-4 py-10 text-center text-sm text-[#8B9895]">
                  {t("document_archive.empty_files")}
                </p>
              ) : view === "grid" ? (
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
                  {visibleFiles.map(renderFileCard)}
                </div>
              ) : (
                <div className="overflow-hidden rounded-[18px] border border-[#EDF0EF]">
                  <div className="hidden grid-cols-[1fr_110px_96px] gap-4 bg-[#FAFBFB] px-[18px] py-2.5 text-xs font-semibold text-[#8B9895] sm:grid">
                    <div>{t("document_archive.col_name")}</div>
                    <div>{t("document_archive.col_modified")}</div>
                    <div>{t("document_archive.col_size")}</div>
                  </div>
                  {visibleFiles.map(renderFileRow)}
                </div>
              )}
            </div>

            <aside className="hidden w-[292px] shrink-0 flex-col gap-5 overflow-y-auto border-l border-[#EDF0EF] bg-[#FBFCFC] px-[22px] py-6 lg:flex">
              {selected && selectedStyle ? (
                <>
                  <div
                    className="flex h-[150px] items-center justify-center rounded-2xl"
                    style={{ background: selectedStyle.tint }}
                  >
                    <FileGlyph
                      ext={selectedStyle.ext}
                      color={selectedStyle.color}
                      large
                    />
                  </div>
                  <div>
                    {editingTitle && canManage ? (
                      <div className="flex flex-col gap-2">
                        <input
                          value={titleDraft}
                          onChange={(e) => setTitleDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveTitle();
                            }
                            if (e.key === "Escape") setEditingTitle(false);
                          }}
                          className="w-full rounded-[14px] border border-[#0F766E] bg-white px-3 py-2 text-sm font-semibold text-[#10221F] outline-none"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingTitle(false)}
                            className="flex h-8 flex-1 items-center justify-center rounded-[16px] border border-[#E5E9E8] text-xs font-medium text-[#4A5A57]"
                          >
                            {t("document_archive.cancel")}
                          </button>
                          <button
                            type="button"
                            disabled={savingTitle}
                            onClick={saveTitle}
                            className="flex h-8 flex-1 items-center justify-center rounded-[16px] bg-[#0F766E] text-xs font-semibold text-white disabled:bg-[#A6B2AF]"
                          >
                            {savingTitle
                              ? t("document_archive.saving")
                              : t("document_archive.save")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1 text-base font-semibold leading-snug tracking-tight text-[#10221F]">
                          {documentDisplayName(selected)}
                        </div>
                        {canManage ? (
                          <button
                            type="button"
                            onClick={() => {
                              setTitleDraft(documentDisplayName(selected));
                              setEditingTitle(true);
                            }}
                            className="mt-0.5 rounded-lg p-1 text-[#8B9895] hover:bg-[#E6F2F0] hover:text-[#0F766E]"
                            title={t("document_archive.edit_title")}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    )}
                    <div className="mt-1 text-[13px] text-[#8B9895]">
                      {formatFileSize(selected?.file?.size_kb)} ·{" "}
                      {relativeTime(selected.updated_at, lang)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => openFile(selected)}
                      className="flex h-9 flex-1 items-center justify-center rounded-[20px] bg-[#0F766E] text-[13px] font-semibold text-white hover:bg-[#0B5A54]"
                    >
                      {t("document_archive.open")}
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadFile(selected)}
                      className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-[20px] border border-[#E5E9E8] text-[13px] font-medium text-[#4A5A57] hover:bg-[#F0F3F2]"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {t("document_archive.download")}
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <div className="group relative flex-1">
                      <button
                        type="button"
                        disabled
                        title={t("document_archive.share_unavailable")}
                        className="flex h-9 w-full cursor-not-allowed items-center justify-center gap-1 rounded-[20px] border border-[#E5E9E8] text-[13px] font-medium text-[#A6B2AF]"
                      >
                        {t("document_archive.share")}
                        <InformationCircleIcon className="h-4 w-4" />
                      </button>
                      <div className="pointer-events-none absolute bottom-[calc(100%+8px)] left-1/2 z-20 hidden w-52 -translate-x-1/2 rounded-[14px] bg-[#10221F] px-3 py-2 text-center text-xs leading-snug text-white shadow-lg group-hover:block">
                        {t("document_archive.share_unavailable")}
                      </div>
                    </div>
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmOpen(true)}
                        className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-[20px] border border-red-200 text-[13px] font-medium text-red-600 hover:bg-red-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                        {t("document_archive.delete")}
                      </button>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-2.5 text-[13px]">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#8B9895]">
                        {t("document_archive.col_modified")}
                      </span>
                      <span className="text-right text-[#10221F]">
                        {relativeTime(selected.updated_at, lang)}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#8B9895]">
                        {t("document_archive.location")}
                      </span>
                      <span className="text-right text-[#10221F]">
                        {breadcrumb.join(" / ")}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-[#8B9895]">
                        {t("document_archive.status")}
                      </span>
                      <span className="text-right text-[#10221F]">
                        {selected.status || "—"}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#8B9895]">
                  {t("document_archive.select_file")}
                </p>
              )}
            </aside>
          </div>
        </div>
      </div>

      {canManage ? (
        <button
          type="button"
          onClick={() => uploadPickerRef.current?.()}
          className="fixed bottom-24 right-5 z-20 flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#0F766E] text-2xl text-white shadow-[0_10px_24px_-8px_rgba(15,118,110,0.85)] md:hidden"
          aria-label={t("document_archive.upload")}
        >
          +
        </button>
      ) : null}

      {canManage && project?.id ? (
        <div className="sr-only" aria-hidden>
          <DocumentUploadSectionV5
            key={`${uploadModule}-${uploadParam}`}
            hideTrigger
            openPickerRef={uploadPickerRef}
            multiple
            module={uploadModule}
            moduleParam={uploadParam}
            folderProjectId={project.id}
            selectedFolder={
              currentFolderId
                ? {
                    id: currentFolderId,
                    displayTitle: currentNode?.name || null,
                  }
                : null
            }
            allowedFileTypes={ARCHIVE_ACCEPT}
            layout="compact"
            onDocumentUploaded={() => setRefreshKey((value) => value + 1)}
          />
        </div>
      ) : null}

      <PMDialog
        open={mobileDetailsOpen && Boolean(selected)}
        onClose={() => setMobileDetailsOpen(false)}
        size="sm"
        title="document_archive.file_details"
        closeOnOutsideClick
        dense
        panelClassName="rounded-[22px] border border-[#EDF0EF] lg:hidden"
      >
        {selected && selectedStyle ? (
          <div className="flex flex-col gap-4 px-1 pb-2 text-left">
            <div
              className="flex h-[120px] items-center justify-center rounded-2xl"
              style={{ background: selectedStyle.tint }}
            >
              <FileGlyph
                ext={selectedStyle.ext}
                color={selectedStyle.color}
                large
              />
            </div>
            <div>
              <div className="text-base font-semibold leading-snug text-[#10221F]">
                {documentDisplayName(selected)}
              </div>
              <div className="mt-1 text-[13px] text-[#8B9895]">
                {formatFileSize(selected?.file?.size_kb)} ·{" "}
                {relativeTime(selected.updated_at, lang)}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => openFile(selected)}
                className="flex h-10 flex-1 items-center justify-center rounded-[20px] bg-[#0F766E] text-[13px] font-semibold text-white hover:bg-[#0B5A54]"
              >
                {t("document_archive.open")}
              </button>
              <button
                type="button"
                onClick={() => downloadFile(selected)}
                className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[20px] border border-[#E5E9E8] text-[13px] font-medium text-[#4A5A57] hover:bg-[#F0F3F2]"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                {t("document_archive.download")}
              </button>
            </div>
            {canManage ? (
              <button
                type="button"
                onClick={() => {
                  setMobileDetailsOpen(false);
                  setDeleteConfirmOpen(true);
                }}
                className="flex h-10 items-center justify-center gap-1.5 rounded-[20px] border border-red-200 text-[13px] font-medium text-red-600 hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
                {t("document_archive.delete")}
              </button>
            ) : null}
          </div>
        ) : null}
      </PMDialog>

      <FolderRenameDialog
        open={Boolean(renameFolder)}
        folder={renameFolder}
        saving={savingRename}
        onClose={() => setRenameFolder(null)}
        onSave={saveFolderRename}
      />

      <FolderAccessDialog
        open={Boolean(accessFolder)}
        folder={accessFolder}
        saving={savingAccess}
        inherited={
          folderRecord(accessFolder)?.effective_access === "admins" &&
          folderRecord(accessFolder)?.access !== "admins"
        }
        onClose={() => setAccessFolder(null)}
        onSave={saveFolderAccess}
      />

      <FolderDeleteDialog
        open={Boolean(deleteFolder)}
        folder={deleteFolder}
        childCount={
          deleteFolder
            ? countDescendants(
                findNode(tree, folderRecord(deleteFolder)?.id)?.node || {
                  children: [],
                }
              )
            : 0
        }
        fileCount={
          deleteFolder
            ? countFilesInFolders(
                documents,
                collectSubtreeIds(
                  findNode(tree, folderRecord(deleteFolder)?.id)?.node
                )
              )
            : 0
        }
        destinationName={
          folderRecord(deleteFolder)?.parent_id
            ? findNode(tree, folderRecord(deleteFolder).parent_id)?.node
                ?.name || t("document_archive.folder_delete_root")
            : t("document_archive.folder_delete_root")
        }
        deleting={deletingFolder}
        onClose={() => setDeleteFolder(null)}
        onConfirm={confirmDeleteFolder}
      />

      <FolderCreateDialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        project={project}
        parentId={currentFolderId}
        isArchiveAdmin={isArchiveAdmin}
        parentRestricted={isAdminsFolder(currentNode)}
        onFolderCreated={() => setRefreshKey((value) => value + 1)}
      />

      <PMDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        size="sm"
        title="document_archive.delete_title"
        closeOnOutsideClick
        panelClassName="rounded-[22px] border border-[#EDF0EF]"
      >
        <div className="px-1 pb-1 text-left">
          <p className="text-sm text-[#6B7B78]">
            {t("document_archive.delete_confirm", {
              name: selected ? documentDisplayName(selected) : "",
            })}
          </p>
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(false)}
              className="flex h-10 flex-1 items-center justify-center rounded-[20px] border border-[#E5E9E8] text-sm font-medium text-[#4A5A57]"
            >
              {t("document_archive.cancel")}
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={confirmDelete}
              className="flex h-10 flex-1 items-center justify-center rounded-[20px] bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-red-300"
            >
              {deleting ? t("document_archive.deleting") : t("document_archive.delete")}
            </button>
          </div>
        </div>
      </PMDialog>
    </div>
  );
}
